import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Input,
  Text,
  LoadingSpinner,
  Select,
  SortableTableHeader,
} from '../../components/ui'
import { HorizontalTableScroll } from './HorizontalTableScroll'
import { financeiroExtratosContaService } from '../../services/financeiroExtratosConta'
import { formatMoneyBr } from '../../utils/financeiroComissoesImport'
import { monthLabel } from '../../utils/financeiroExtratosConta'
import type {
  FinanceiroPelStatement,
  FinanceiroPelStatementType,
} from '../../types/financeiroExtratosConta'
import {
  CARD_CLASS,
  ERROR_BANNER_CLASS,
  FIELD_LABEL_CLASS,
  FILTER_CARD_CLASS,
  PAGE_SIZE,
  PAGINATION_BTN_CLASS,
  formatDateTimePtBr,
  formatFolderDatePtBr,
  paginationPageClass,
} from './financeiroPelUi'

type SortKey =
  | 'transaction_at'
  | 'account'
  | 'type'
  | 'amount'
  | 'description'
  | 'identification'
  | 'file_name'
  | 'folder_date'

const COLUMN_DEFS: Array<{ key: SortKey; label: string }> = [
  { key: 'folder_date', label: 'Data pasta' },
  { key: 'transaction_at', label: 'Data/hora' },
  { key: 'account', label: 'Conta' },
  { key: 'type', label: 'Tipo' },
  { key: 'amount', label: 'Valor' },
  { key: 'identification', label: 'Identificação' },
  { key: 'description', label: 'Descrição' },
  { key: 'file_name', label: 'Arquivo' },
]

function formatType(type: FinanceiroPelStatementType | null): string {
  if (type === 'debit') return 'Débito'
  if (type === 'credit') return 'Crédito'
  return '—'
}

const ExtratosContaConsultaTab: React.FC = () => {
  const [years, setYears] = useState<string[]>([])
  const [months, setMonths] = useState<string[]>([])
  const [days, setDays] = useState<string[]>([])
  const [accounts, setAccounts] = useState<string[]>([])
  const [rows, setRows] = useState<FinanceiroPelStatement[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [year, setYear] = useState('')
  const [month, setMonth] = useState('')
  const [day, setDay] = useState('')
  const [account, setAccount] = useState('')
  const [type, setType] = useState<'' | FinanceiroPelStatementType>('')
  const [folderDateFrom, setFolderDateFrom] = useState('')
  const [folderDateTo, setFolderDateTo] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [search, setSearch] = useState('')

  const [appliedYear, setAppliedYear] = useState('')
  const [appliedMonth, setAppliedMonth] = useState('')
  const [appliedDay, setAppliedDay] = useState('')
  const [appliedAccount, setAppliedAccount] = useState('')
  const [appliedType, setAppliedType] = useState<'' | FinanceiroPelStatementType>('')
  const [appliedFolderDateFrom, setAppliedFolderDateFrom] = useState('')
  const [appliedFolderDateTo, setAppliedFolderDateTo] = useState('')
  const [appliedDateFrom, setAppliedDateFrom] = useState('')
  const [appliedDateTo, setAppliedDateTo] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')

  const [sortBy, setSortBy] = useState<SortKey>('transaction_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const requestIdRef = useRef(0)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const startIndex = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE
  const endIndex = Math.min(startIndex + PAGE_SIZE, total)

  const dbSortBy = sortBy === 'folder_date' ? 'year' : sortBy

  const loadFilterOptions = useCallback(async () => {
    try {
      const [y, m, d, a] = await Promise.all([
        financeiroExtratosContaService.listStatementFilterYears(),
        financeiroExtratosContaService.listStatementFilterMonths(year || null),
        financeiroExtratosContaService.listStatementFilterDays(year || null, month || null),
        financeiroExtratosContaService.listStatementFilterAccounts(
          year || null,
          month || null,
          day || null
        ),
      ])
      setYears(y)
      setMonths(m)
      setDays(d)
      setAccounts(a)
      setMonth((prev) => (prev && !m.includes(prev) ? '' : prev))
      setDay((prev) => (prev && !d.includes(prev) ? '' : prev))
      setAccount((prev) => (prev && !a.includes(prev) ? '' : prev))
    } catch (err) {
      console.error(err)
    }
  }, [year, month, day])

  const loadRows = useCallback(async () => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    try {
      const result = await financeiroExtratosContaService.listStatements({
        year: appliedYear || null,
        month: appliedMonth || null,
        day: appliedDay || null,
        account: appliedAccount || null,
        type: appliedType || null,
        folderDateFrom: appliedFolderDateFrom || null,
        folderDateTo: appliedFolderDateTo || null,
        dateFrom: appliedDateFrom || null,
        dateTo: appliedDateTo || null,
        search: appliedSearch || null,
        sortBy: dbSortBy,
        sortOrder,
        page: currentPage,
        pageSize: PAGE_SIZE,
      })
      if (requestId !== requestIdRef.current) return
      setRows(result.rows)
      setTotal(result.total)
    } catch (err) {
      if (requestId !== requestIdRef.current) return
      console.error(err)
      setError(err instanceof Error ? err.message : 'Erro ao carregar extratos.')
      setRows([])
      setTotal(0)
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [
    appliedYear,
    appliedMonth,
    appliedDay,
    appliedAccount,
    appliedType,
    appliedFolderDateFrom,
    appliedFolderDateTo,
    appliedDateFrom,
    appliedDateTo,
    appliedSearch,
    dbSortBy,
    sortOrder,
    currentPage,
  ])

  useEffect(() => {
    void loadFilterOptions()
  }, [loadFilterOptions])

  useEffect(() => {
    if (!searched) return
    void loadRows()
  }, [loadRows, searched])

  const applyFilters = () => {
    if (folderDateFrom && folderDateTo && folderDateTo < folderDateFrom) {
      requestIdRef.current += 1
      setError('A data final da pasta deve ser maior ou igual à inicial.')
      setRows([])
      setTotal(0)
      setSearched(false)
      setLoading(false)
      return
    }
    if (dateFrom && dateTo && dateTo < dateFrom) {
      requestIdRef.current += 1
      setError('A data/hora final deve ser maior ou igual à inicial.')
      setRows([])
      setTotal(0)
      setSearched(false)
      setLoading(false)
      return
    }

    setAppliedYear(year)
    setAppliedMonth(month)
    setAppliedDay(day)
    setAppliedAccount(account)
    setAppliedType(type)
    setAppliedFolderDateFrom(folderDateFrom)
    setAppliedFolderDateTo(folderDateTo)
    setAppliedDateFrom(dateFrom)
    setAppliedDateTo(dateTo)
    setAppliedSearch(search.trim())
    setCurrentPage(1)
    setSearched(true)
  }

  const clearFilters = () => {
    requestIdRef.current += 1
    setYear('')
    setMonth('')
    setDay('')
    setAccount('')
    setType('')
    setFolderDateFrom('')
    setFolderDateTo('')
    setDateFrom('')
    setDateTo('')
    setSearch('')
    setAppliedYear('')
    setAppliedMonth('')
    setAppliedDay('')
    setAppliedAccount('')
    setAppliedType('')
    setAppliedFolderDateFrom('')
    setAppliedFolderDateTo('')
    setAppliedDateFrom('')
    setAppliedDateTo('')
    setAppliedSearch('')
    setCurrentPage(1)
    setSearched(false)
    setRows([])
    setTotal(0)
    setError(null)
    setLoading(false)
  }

  const toggleSort = (key: string) => {
    const sortKey = key as SortKey
    if (sortBy === sortKey) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(sortKey)
      setSortOrder(sortKey === 'transaction_at' || sortKey === 'amount' ? 'desc' : 'asc')
    }
    setCurrentPage(1)
  }

  const cellValue = (row: FinanceiroPelStatement, key: SortKey): string => {
    switch (key) {
      case 'transaction_at':
        return formatDateTimePtBr(row.transaction_at)
      case 'type':
        return formatType(row.type)
      case 'amount':
        return formatMoneyBr(row.amount)
      case 'folder_date':
        return formatFolderDatePtBr(row.year, row.month, row.day)
      case 'identification':
        return row.identification?.trim() ? row.identification : '—'
      case 'description':
        return row.description?.trim() ? row.description : '—'
      case 'account':
        return row.account?.trim() ? row.account : '—'
      case 'file_name':
        return row.file_name?.trim() ? row.file_name : '—'
      default:
        return '—'
    }
  }

  return (
    <div className="space-y-6">
      {error && <div className={ERROR_BANNER_CLASS}>{error}</div>}

      <form
        className={FILTER_CARD_CLASS}
        onSubmit={(event) => {
          event.preventDefault()
          applyFilters()
        }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Ano</label>
            <Select
              className="w-full"
              value={year}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setYear(e.target.value)}
            >
              <option value="">Todos</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Mês</label>
            <Select
              className="w-full"
              value={month}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setMonth(e.target.value)}
            >
              <option value="">Todos</option>
              {months.map((m) => (
                <option key={m} value={m}>
                  {`${m} — ${monthLabel(m)}`}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Dia</label>
            <Select
              className="w-full"
              value={day}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDay(e.target.value)}
            >
              <option value="">Todos</option>
              {days.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Data da pasta inicial</label>
            <Input
              type="date"
              className="w-full min-w-0"
              value={folderDateFrom}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFolderDateFrom(e.target.value)
              }
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Data da pasta final</label>
            <Input
              type="date"
              className="w-full min-w-0"
              value={folderDateTo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFolderDateTo(e.target.value)}
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Conta</label>
            <Select
              className="w-full"
              value={account}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAccount(e.target.value)}
            >
              <option value="">Todas</option>
              {accounts.map((acc) => (
                <option key={acc} value={acc}>
                  {acc}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Tipo</label>
            <Select
              className="w-full"
              value={type}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setType(e.target.value as '' | FinanceiroPelStatementType)
              }
            >
              <option value="">Todos</option>
              <option value="debit">Débito</option>
              <option value="credit">Crédito</option>
            </Select>
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Data/hora inicial</label>
            <Input
              type="date"
              className="w-full min-w-0"
              value={dateFrom}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Data/hora final</label>
            <Input
              type="date"
              className="w-full min-w-0"
              value={dateTo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateTo(e.target.value)}
            />
          </div>
          <div className="min-w-0 sm:col-span-2">
            <label className={FIELD_LABEL_CLASS}>Busca</label>
            <Input
              className="w-full"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              placeholder="Descrição, observação ou identificação..."
            />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={loading}>
            Buscar
          </Button>
          <Button type="button" variant="outline" onClick={clearFilters} disabled={loading}>
            Limpar
          </Button>
        </div>
      </form>

      <div className={CARD_CLASS}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <HorizontalTableScroll showNavButtons={false}>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    {COLUMN_DEFS.map((col) => (
                      <SortableTableHeader
                        key={col.key}
                        sortKey={col.key}
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        compact
                      >
                        {col.label}
                      </SortableTableHeader>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={COLUMN_DEFS.length}
                        className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                      >
                        {searched
                          ? 'Nenhum registro encontrado'
                          : 'Clique em Buscar para carregar os resultados'}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr
                        key={row.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        {COLUMN_DEFS.map((col) => (
                          <td
                            key={col.key}
                            className={`px-3 py-2 text-sm whitespace-nowrap text-gray-900 dark:text-gray-100 ${
                              col.key === 'amount' ? 'tabular-nums text-right' : ''
                            } ${col.key === 'description' ? 'max-w-xs truncate' : ''}`}
                            title={col.key === 'description' ? cellValue(row, col.key) : undefined}
                          >
                            {cellValue(row, col.key)}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </HorizontalTableScroll>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                  {total === 0
                    ? 'Nenhum registro'
                    : `Mostrando ${startIndex + 1} a ${endIndex} de ${total} registro${total !== 1 ? 's' : ''}`}
                </Text>

                {totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={PAGINATION_BTN_CLASS}
                    >
                      Anterior
                    </button>
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber: number
                        if (totalPages <= 5) {
                          pageNumber = i + 1
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i
                        } else {
                          pageNumber = currentPage - 2 + i
                        }
                        return (
                          <button
                            key={pageNumber}
                            type="button"
                            onClick={() => setCurrentPage(pageNumber)}
                            className={paginationPageClass(currentPage === pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        )
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={PAGINATION_BTN_CLASS}
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ExtratosContaConsultaTab
